import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { ensureProtocol } from '@elfsight/lib-utils';
import { useSettings } from '@wu/widget-core';
import classNames from 'classnames';
import { noop } from 'es-toolkit';
import {
  APP_CLASSNAME_PREFIX,
  ARROWS_CONTAINER_HEIGHT,
  IMAGE_OR_VIDEO_SLIDE_MIN_HEIGHT
} from '../../../../constants';
import { Background } from '../Background';
import { Elements } from '../Elements';
import { Media } from '../element-variations';

const DEFAULT_COMPONENT_PADDING = 20;
const PADDING_FOR_ARROWS = ARROWS_CONTAINER_HEIGHT - DEFAULT_COMPONENT_PADDING;

function calculateSlidePadding({
  navigationElementsVisibility,
  arrowsPosition,
  paginationHeight
}) {
  const padding = {
    top: 0,
    bottom: 0
  };

  const { arrows, pagination } = navigationElementsVisibility;

  if (arrows && arrowsPosition.startsWith('top')) {
    padding.top = PADDING_FOR_ARROWS;
  }

  if (pagination) {
    padding.bottom = paginationHeight - DEFAULT_COMPONENT_PADDING;
  } else if (arrows && arrowsPosition.startsWith('bottom')) {
    padding.bottom = PADDING_FOR_ARROWS;
  }

  return padding;
}

export const Slide = ({
  data,
  onSlideLinkClick,
  active,
  isDrag,
  isMobile,
  paginationHeight,
  navigationElementsVisibility,
  googleAnalyticsExtraParameters,
  shouldForceMinHeight,
  cloned = false
}) => {
  const {
    imageEffect,
    backgroundMediaImage,
    backgroundMediaVideo,
    contentMediaImage,
    contentMediaVideo,
    contentWidth,
    contentHorizontalPosition,
    contentVerticalPosition,
    contentLayout,
    contentOverlayEnabled,
    contentOverlayColor,
    imageLink,
    contentMediaEnabled,
    contentTitleEnabled,
    contentTextEnabled,
    contentButtonEnabled,
    contentBadgeEnabled
  } = data;

  const [mediaData, setMediaData] = useState({
    width: 0,
    height: 0
  });
  const backgroundRef = useRef(null);
  const elementsRef = useRef(null);
  const wrapperRef = useRef(null);

  const { editMode, arrowsPosition } = useSettings();

  const { current: backgroundElement } = backgroundRef;
  const { current: elementsElement } = elementsRef;
  const { current: wrapper } = wrapperRef;

  const padding = calculateSlidePadding({
    navigationElementsVisibility,
    arrowsPosition,
    paginationHeight
  });

  const contentMedia = contentMediaImage || contentMediaVideo;
  const media = backgroundMediaImage || backgroundMediaVideo;
  const { rawValue: imageLinkRawValue, target: imageLinkTarget } =
    imageLink ?? {};
  const link = ensureProtocol(imageLinkRawValue);
  const linkTarget =
    editMode || imageLinkTarget === '_blank' ? '_blank' : '_top';
  const [isVerticalLayout, isHorizontalLayout] = useMemo(
    () => [
      ['top', 'bottom'].includes(contentLayout),
      ['left', 'right'].includes(contentLayout)
    ],
    [contentLayout]
  );
  const isShowContent =
    contentTitleEnabled ||
    contentTextEnabled ||
    contentButtonEnabled ||
    contentBadgeEnabled ||
    contentMediaEnabled;
  const isShowMedia =
    contentMedia && contentLayout !== 'center' && contentMediaEnabled;
  const isCloned = cloned.toString() === 'true';
  const isAbsolute = isVerticalLayout || contentLayout === 'center';

  const event = useCallback(
    (e) => {
      const x = e.clientX;
      const y = e.clientY;

      if (backgroundElement) {
        backgroundElement.style.left = `-${x / 1200}%`;
        backgroundElement.style.top = `-${y / 1200}%`;
      }

      if (elementsElement) {
        elementsElement.style.transform = `translateX(-${
          50 - x / 500
        }%) translateY(-${50 - y / 500}%)`;
      }
    },
    [backgroundElement, elementsElement]
  );

  const handleInnerClick = useCallback(
    () => backgroundRef.current?.click(),
    []
  );

  const handleClick = useCallback(
    (e) => {
      if (isDrag) {
        e.preventDefault();
      }

      if (imageLinkRawValue) {
        onSlideLinkClick?.(link);
      }
    },
    [isDrag, link, imageLinkRawValue, onSlideLinkClick]
  );

  const handleDrag = useCallback((e) => e.preventDefault(), []);

  useLayoutEffect(() => {
    if (active && wrapper && imageEffect === 'parallax') {
      wrapper.addEventListener('mousemove', event, { passive: false });
    }

    return () => {
      if (wrapper) {
        wrapper.removeEventListener('mousemove', event);
      }
    };
  }, [event, active, imageEffect, wrapper]);

  const backgroundProps = imageLinkRawValue
    ? {
        href: link,
        onClick: handleClick,
        onDragStart: handleDrag,
        rel: 'noreferrer noopener',
        target: linkTarget,
        'aria-label': `Redirect to ${link}`
      }
    : {};

  return (
    <Container
      ref={wrapperRef}
      $layout={contentLayout}
      $active={active}
      $hasLink={!!imageLinkRawValue}
      $shouldForceMinHeight={shouldForceMinHeight}
    >
      <StyledBackgroundWrapper
        ref={backgroundRef}
        as={imageLinkRawValue ? 'a' : 'div'}
        {...backgroundProps}
      >
        {media && (
          <Background
            data={data}
            active={active}
            cloned={isCloned}
            onReady={noop}
          />
        )}
      </StyledBackgroundWrapper>

      {contentOverlayEnabled && <Overlay $color={contentOverlayColor} />}

      {isShowContent && (
        <Inner
          ref={elementsRef}
          onClick={handleInnerClick}
          $layout={contentLayout}
          $isMobile={isMobile}
          $contentWidth={contentWidth}
          $padding={padding}
          $horizontalPos={contentHorizontalPosition}
          $verticalPos={contentVerticalPosition}
          $hasMedia={!!contentMedia}
        >
          <ElementsWrapper
            $active={active}
            $layout={contentLayout}
            $isMobile={isMobile}
          >
            <Elements
              googleAnalyticsExtraParameters={googleAnalyticsExtraParameters}
              data={data}
              isDrag={isDrag}
              isMobile={isMobile}
            />
          </ElementsWrapper>

          {isShowMedia && (
            <Media
              data={data}
              active={active}
              isMobile={isMobile}
              isVerticalLayout={isVerticalLayout}
              isHorizontalLayout={isHorizontalLayout}
              absolute={isAbsolute}
              cloned={isCloned}
              mediaData={mediaData}
              setMediaData={setMediaData}
            />
          )}
        </Inner>
      )}
    </Container>
  );
};

const Container = styled.div.attrs(({ $layout, $active }) => ({
  className: classNames(
    `${APP_CLASSNAME_PREFIX}-slide-component`,
    `${APP_CLASSNAME_PREFIX}-slide-${$layout}`,
    `${APP_CLASSNAME_PREFIX}-slide-animate`,
    {
      [`${APP_CLASSNAME_PREFIX}-slide-active`]: $active
    }
  )
}))`
  display: flex;
  padding: 20px;
  position: relative;
  justify-content: flex-start;
  align-items: flex-start;
  flex-grow: 1;

  cursor: ${({ $hasLink }) => ($hasLink ? 'pointer' : 'inherit')};
  min-height: ${({ $shouldForceMinHeight }) =>
    $shouldForceMinHeight ? IMAGE_OR_VIDEO_SLIDE_MIN_HEIGHT : '100%'};
`;

const StyledBackgroundWrapper = styled.div.attrs({
  className: `${APP_CLASSNAME_PREFIX}-slide-background`
})`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 0;
  -webkit-user-drag: none;
  display: block;
`;

const Overlay = styled.div.attrs({
  className: `${APP_CLASSNAME_PREFIX}-slide-overlay`
})`
  position: absolute;
  inset: 0;
  z-index: 2;
  pointer-events: none;

  background-color: ${({ $color }) => $color};
`;

const Inner = styled.div.attrs({
  className: `${APP_CLASSNAME_PREFIX}-slide-inner`
})`
  display: flex;
  z-index: 3;
  width: 80%;
  max-height: 100%;
  position: relative;
  transform: translate(-50%, -50%);
  height: 100%;
  align-items: center;

  padding: ${({ $padding }) => `${$padding.top}px 0 ${$padding.bottom}px`};
  max-width: ${({ $contentWidth }) => $contentWidth ?? 100}%;

  left: ${({ $isMobile, $horizontalPos }) =>
    $isMobile ? '50%' : `${$horizontalPos}%`};
  top: ${({ $isMobile, $verticalPos }) =>
    $isMobile ? '50%' : `${$verticalPos}%`};

  ${({ $layout }) => {
    switch ($layout) {
      case 'center':
        return css`
          flex-direction: column;
          align-items: center;
          justify-content: center;
        `;
      case 'top':
        return css`
          flex-direction: column;
        `;
      case 'bottom':
        return css`
          flex-direction: column-reverse;
        `;
      case 'left':
        return css`
          flex-direction: row;
        `;
      case 'right':
        return css`
          flex-direction: row-reverse;
        `;
      default:
        return '';
    }
  }}

  ${({ $isMobile, $layout, $hasMedia }) =>
    $isMobile &&
    css`
      text-align: center;
      ${$layout !== 'bottom' && 'flex-direction: column;'}
      ${!$hasMedia && 'justify-content: center;'}
    `}
`;

const ElementsWrapper = styled.div.attrs({
  className: `${APP_CLASSNAME_PREFIX}-slide-elements`
})`
  width: 50%;
  flex-shrink: 0;
  z-index: 3;
  position: relative;
  transition:
    0.3s ease bottom,
    0.3s ease opacity;
  bottom: -10px;
  opacity: 0;
  padding: 0 20px;

  ${({ $active }) =>
    $active &&
    css`
      bottom: 0;
      opacity: 1;
    `}

  ${({ $layout }) =>
    ['top', 'bottom', 'center'].includes($layout) &&
    css`
      width: 100%;
      text-align: center;
    `}

  ${({ $isMobile }) =>
    $isMobile &&
    css`
      width: 100%;
      text-align: center;
    `}
`;
